import { NavLink, useNavigate } from 'react-router-dom';
import { useCreateThread, useThreads } from '../api/hooks';
import { Badge, Button, Spinner } from '../components/ui';
import { zh } from '../i18n';

/**
 * The list pane of the desktop two-pane layout (SPEC §10: "desktop >=1024 px =
 * left rail + two panes").
 *
 * It is rendered inside the conversation route and hidden by CSS below 1024 px,
 * so there is one DOM order and one focus order at every width — the same rule
 * `BottomTabs` follows for the rail. `useThreads` is the query 對話列表 already
 * holds, so opening a thread reads the cache rather than refetching, and
 * `NavLink` marks the open thread with `aria-current="page"`: the accessible
 * state and the styling hook are the same attribute and cannot drift apart.
 *
 * Deliberately thinner than 對話列表's own rows: a pane beside a conversation
 * answers "which thread am I in, and what else is waiting", not "what did each
 * thread cost". Cost chips, the archive toggle and the archived filter stay on
 * the full-width screen, where there is room for them.
 */
export function ThreadsPane(): JSX.Element {
  const threads = useThreads(false);
  const createThread = useCreateThread();
  const navigate = useNavigate();

  const items = threads.data?.pages.flatMap((page) => page.items) ?? [];

  return (
    <div className="pane">
      <div className="pane__head">
        <h2 className="pane__title">{zh.threads.title}</h2>
        <Button
          variant="outline"
          small
          disabled={createThread.isPending}
          onClick={() =>
            createThread.mutate({}, { onSuccess: (thread) => navigate(`/c/${thread.id}`) })
          }
        >
          {createThread.isPending ? zh.threads.creating : zh.threads.newThread}
        </Button>
      </div>

      <nav className="pane__list" aria-label={zh.threads.listLabel}>
        {threads.isLoading ? (
          <div className="pane__loading">
            <Spinner />
          </div>
        ) : null}
        <ul className="list">
          {items.map((item) => (
            <li key={item.id}>
              <NavLink to={`/c/${item.id}`} className="list__row">
                <div className="list__main">
                  <div className="list__title">{item.title || zh.threads.untitled}</div>
                  <div className="list__sub">
                    {item.preview || zh.threads.messageCount(item.messageCount)}
                  </div>
                </div>
                {item.hasPendingApproval ? (
                  <Badge tone="primary">{zh.threads.pendingApproval}</Badge>
                ) : null}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
}
